import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import type { Collection } from '../collection'
import { Selection } from '../models'

import type { Feature } from './feature'

/** Management values exposed through the owning knowledge-base collection. */
export class Selectors {
  /** Private agents available in the loaded management inventory. */
  public readonly agents

  /** Whether the selected file, target, and destination are ready for upload. */
  public readonly canUpload

  /** Version used to recreate the file input after its value is cleared. */
  public readonly fileInputVersion

  /** Whether the Agent-specific workspace is active. */
  public readonly isAgentMode

  /** Whether the management inventory is loading. */
  public readonly isLoading

  /** Whether the Shared workspace is active. */
  public readonly isSharedMode

  /** Whether tenants are being loaded for the Shared workspace. */
  public readonly isLoadingTenants

  /** Whether zones are being loaded for the selected tenant. */
  public readonly isLoadingZones

  /** Active Agent or Shared document workspace. */
  public readonly mode

  /** User-facing inventory or file-selection failure. */
  public readonly operationError

  /** Canonical browser selection stored by the entity collection. */
  public readonly selection

  /** Currently selected agent identifier. */
  public readonly selectedAgentId

  /** Currently selected private agent and its manageable data sources. */
  public readonly selectedAgent

  /** Currently selected knowledge-base data source. */
  public readonly selectedDataSource

  /** Valid file selected for upload. */
  public readonly selectedFile

  /** Selected tenant identifier for the Shared workspace. */
  public readonly selectedTenantId

  /** Selected zone identifier for the Shared workspace. */
  public readonly selectedZoneId

  /** Whether the data-source selector is needed for the selected agent. */
  public readonly showDataSourceSelector

  /** Completed target represented by the active workspace selection. */
  public readonly target

  /** Tenant or Zone audience selected in the Shared workspace. */
  public readonly targetType

  /** Tenants available as shared document audiences. */
  public readonly tenants

  /** User-facing tenant-loading failure. */
  public readonly tenantsError

  /** Zones available for the currently selected tenant. */
  public readonly zones

  /** User-facing zone-loading failure. */
  public readonly zonesError

  constructor(feature: Feature, collection: Collection) {
    const inventoryState = feature.state.project(state => state.inventory)
    const inventory = inventoryState.project(entry => entry.data)
    const agents = inventory.project(value => value?.agents ?? [])
    const selectedFileState = feature.state.project(state => state.selectedFile)
    const tenantState = feature.state.project(state => state.tenants)
    const tenants = tenantState.project(entry => entry.data ?? [])
    const zoneRequest = feature.state.project(state => state.zones)
    const selection =
      collection.plugins.currentListPlugin.appliedParams.selectors.filters.project(
        value => value ?? Selection.INITIAL,
      )
    const mode = selection.project(value => value.mode)
    const selectedAgentId = selection.project(value => value.agent.agentId)
    const selectedTenantId = selection.project(value => value.shared.tenantId)
    const selectedZoneId = selection.project(value => value.shared.zoneId)
    const targetType = selection.project(value => value.shared.targetType)
    const selectedAgent = agents.project(
      selectedAgentId.asSelector,
      (values, agentId) =>
        values.find(candidate => candidate.agentId === agentId),
    )
    const selectedDataSource = selectedAgent.project(
      selection.asSelector,
      (agent, value) =>
        agent?.dataSources.find(
          dataSource =>
            dataSource.id === value.agent.dataSource?.dataSourceId &&
            dataSource.knowledgeBaseId ===
              value.agent.dataSource.knowledgeBaseId,
        ),
    )
    const selectedTenant = tenants.project(
      selectedTenantId.asSelector,
      (values, tenantId) => values.find(tenant => tenant.id === tenantId),
    )
    const zones = zoneRequest.project(
      selectedTenantId.asSelector,
      (request, tenantId) =>
        request !== undefined && request.tenantId === tenantId
          ? (request.entry.data ?? [])
          : [],
    )
    const selectedZone = zones.project(
      selectedZoneId.asSelector,
      (values, zoneId) => values.find(zone => zone.id === zoneId),
    )
    const target = selection.project(
      selectedTenant.asSelector,
      selectedZone.asSelector,
      (
        value,
        tenant,
        zone,
      ):
        | ents.core.agent.KnowledgeBase.Target.Platform
        | ents.core.agent.KnowledgeBase.Target.Tenant
        | ents.core.agent.KnowledgeBase.Target.Zone
        | undefined => {
        if (value.mode === Selection.Mode.Agent) {
          return { type: ents.core.agent.KnowledgeBase.Target.Type.Platform }
        }
        const sharedTarget = Selection.getSharedTarget(value.shared)
        if (
          sharedTarget?.type ===
            ents.core.agent.KnowledgeBase.Target.Type.Tenant &&
          tenant?.id === sharedTarget.tenantId
        ) {
          return sharedTarget
        }
        if (
          sharedTarget?.type ===
            ents.core.agent.KnowledgeBase.Target.Type.Zone &&
          tenant?.id === sharedTarget.tenantId &&
          zone?.id === sharedTarget.zoneId
        ) {
          return sharedTarget
        }
        return undefined
      },
    )
    this.agents = agents
    this.isAgentMode = mode.project(value => value === Selection.Mode.Agent)
    this.isLoading = inventoryState.project(entry => entry.loading === true)
    this.isSharedMode = mode.project(value => value === Selection.Mode.Shared)
    this.isLoadingTenants = tenantState.project(entry => entry.loading === true)
    this.isLoadingZones = zoneRequest.project(
      selectedTenantId.asSelector,
      (request, tenantId) =>
        request !== undefined &&
        request.tenantId === tenantId &&
        request.entry.loading === true,
    )
    this.mode = mode
    this.selection = selection
    this.selectedAgentId = selectedAgentId
    this.selectedAgent = selectedAgent
    this.selectedDataSource = selectedDataSource
    this.selectedFile = selectedFileState.project(entry => entry?.data)
    this.selectedTenantId = selectedTenantId
    this.selectedZoneId = selectedZoneId
    this.fileInputVersion = feature.state.project(
      state => state.fileInputVersion,
    )
    this.showDataSourceSelector = selectedAgent.project(
      agent => (agent?.dataSources.length ?? 0) > 1,
    )
    this.target = target
    this.targetType = targetType
    this.tenants = tenants
    this.tenantsError = tenantState.project(entry => _errorMessage(entry.error))
    this.zones = zones
    this.zonesError = zoneRequest.project(
      selectedTenantId.asSelector,
      (request, tenantId) =>
        request !== undefined && request.tenantId === tenantId
          ? _errorMessage(request.entry.error)
          : undefined,
    )
    this.operationError = feature.state.project(state =>
      _errorMessage(
        state.selectedFile?.error ??
          state.inventory.error ??
          state.tenants.error ??
          state.zones?.entry.error,
      ),
    )
    this.canUpload = this.selectedFile.project(
      mode.asSelector,
      selectedDataSource.asSelector,
      target.asSelector,
      (file, selectedMode, dataSource, uploadTarget) =>
        TypeChecker.isNotNullish(file) &&
        ((selectedMode === Selection.Mode.Agent &&
          TypeChecker.isNotNullish(dataSource)) ||
          (selectedMode === Selection.Mode.Shared &&
            (ents.core.agent.KnowledgeBase.Target.Tenant.isTenant(
              uploadTarget,
            ) ||
              ents.core.agent.KnowledgeBase.Target.Zone.isZone(uploadTarget)))),
    )
  }
}

/** Converts a stored failure into a user-facing explanation. */
function _errorMessage(error: Error | undefined): string | undefined {
  if (error === undefined) {
    return undefined
  }
  return error.message || 'The knowledge base operation could not be completed.'
}
